/**
 * DOEAnalyzerPage
 * ---------------
 *
 * September 2026
 *
 * Design-of-Experiment analytics for a completed experiment: design-type
 * classification, ANOVA/sensitivity/percentile/bottleneck charts and a
 * "Fundamental Questions" summary (computed server-side by
 * backend/analysis/doe_stats.py -> experiment_chart.generate_experiment_charts()),
 * plus an AI-generated narrative via a connected provider that also answers
 * the Sacks et al. (1989) framework's remaining categories qualitatively and
 * folds in engine trial-log findings when available.
 */
import * as React from "react";
import axios from "axios";
import { Button } from "primereact/button";
import { Tag } from "primereact/tag";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { MultiSelect } from "primereact/multiselect";
import { InputTextarea } from "primereact/inputtextarea";
import { TabView, TabPanel } from "primereact/tabview";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { Link } from "react-router-dom";
import ExperimentPicker from "./ExperimentPicker.js";
import { narrativeInput, summarizeForAssistant } from "./assistantSummary.js";
import SystemPromptEditor, { SYSTEM_PROMPT_OVERRIDE_KEY } from "./SystemPromptEditor.js";
import useAIProviderStatus, { describeProviderStatus } from "../Settings/useAIProviderStatus.js";
import NarrativeChat from "./NarrativeChat.js";
import { renderChart } from "./charts/renderChart.js";
import { getAvailableChartTabs, getDefaultChartTabKeys } from "./charts/chartTabs.js";
import { deriveDesignInfo } from "./charts/analyticsDesign.js";
import { DESIGN_TYPE_LABELS, DESIGN_TYPE_SEVERITY } from "./charts/analyticsConstants.js";
import FundamentalQuestionsSection from "./charts/FundamentalQuestionsSection.js";
import BestWorstTrial from "./charts/BestWorstTrial.js";
import "./DOEAnalyzer.css";

const POLL_INTERVAL_MS = 2000;

function readSystemPromptOverride() {
  try {
    return window.localStorage.getItem(SYSTEM_PROMPT_OVERRIDE_KEY) || undefined;
  } catch {
    return undefined;
  }
}

function pollJob(jobId, onComplete, onError) {
  axios
    .get(`/api/experiment/job/${jobId}/status`)
    .then((res) => {
      const { status } = res.data;
      if (status === "complete") {
        onComplete(res.data.result);
      } else if (status === "error") {
        onError(res.data.error || "Analysis failed.");
      } else {
        setTimeout(() => pollJob(jobId, onComplete, onError), POLL_INTERVAL_MS);
      }
    })
    .catch((err) => onError(err.response?.data?.error || "Failed to check analysis status."));
}

/**
 * @param {Function} onSummaryChange - Called with a summary of the results for the AI Assistant.
 */
function DOEAnalyzerPage({ experiment_ids, onSummaryChange = () => {} }) {
  const [selectedExperimentId, setSelectedExperimentId] = React.useState(null);
  const [selectedExperiment, setSelectedExperiment] = React.useState(null);
  // The experiment the shown results are for (the picker may have moved on since).
  const [analyzedExperiment, setAnalyzedExperiment] = React.useState(null);
  const [chartData, setChartData] = React.useState(null);
  const [analyzing, setAnalyzing] = React.useState(false);
  const [analyzeError, setAnalyzeError] = React.useState(null);

  const [visibleChartKeys, setVisibleChartKeys] = React.useState([]);
  const [activeChartTab, setActiveChartTab] = React.useState(0);
  const [visibleQuestionKeys, setVisibleQuestionKeys] = React.useState([]);

  const providerStatus = useAIProviderStatus();
  const [narrative, setNarrative] = React.useState(null);
  const [generatingNarrative, setGeneratingNarrative] = React.useState(false);
  const [narrativeError, setNarrativeError] = React.useState(null);
  const [userObjective, setUserObjective] = React.useState("");

  React.useEffect(() => {
    onSummaryChange(summarizeForAssistant(chartData, narrative, analyzedExperiment));
  }, [chartData, narrative, analyzedExperiment]);
  // Leaving the page drops its results.
  React.useEffect(() => () => onSummaryChange(null), []);

  const runAnalysis = () => {
    if (!selectedExperimentId) return;
    const experiment = selectedExperiment;
    setAnalyzing(true);
    setAnalyzeError(null);
    setChartData(null);
    setNarrative(null);
    axios
      .post("/api/doe/analyze", { experiment_ids: [selectedExperimentId] })
      .then((res) => {
        pollJob(
          res.data.job_id,
          (result) => {
            setChartData(result);
            setAnalyzedExperiment(experiment);
            const metadata = result._metadata || {};
            const design = deriveDesignInfo(result, metadata, metadata.experiment_type);
            const available = getAvailableChartTabs(result, metadata);
            setVisibleChartKeys(getDefaultChartTabKeys(available, design));
            setVisibleQuestionKeys(Object.keys(result.fundamental_questions || {}));
            setAnalyzing(false);
          },
          (message) => {
            setAnalyzeError(message);
            setAnalyzing(false);
          },
        );
      })
      .catch((err) => {
        setAnalyzeError(err.response?.data?.error || "Failed to start analysis.");
        setAnalyzing(false);
      });
  };

  const [narrativeCopied, setNarrativeCopied] = React.useState(false);

  const copyNarrative = () => {
    if (!narrative) return;
    navigator.clipboard
      .writeText(narrative)
      .then(() => {
        setNarrativeCopied(true);
        setTimeout(() => setNarrativeCopied(false), 2000);
      })
      .catch(() => {});
  };

  const generateNarrative = () => {
    if (!chartData) return;
    setGeneratingNarrative(true);
    setNarrativeError(null);
    axios
      .post("/api/doe/narrative", {
        doe_result: narrativeInput(chartData),
        user_objective: userObjective.trim() || undefined,
        system_prompt_override: readSystemPromptOverride(),
      })
      .then((res) => setNarrative(res.data.narrative))
      .catch((err) => {
        setNarrativeError(err.response?.data?.error || "Failed to generate narrative.");
      })
      .finally(() => setGeneratingNarrative(false));
  };

  const metadata = chartData?._metadata || {};
  const design = chartData ? deriveDesignInfo(chartData, metadata, metadata.experiment_type) : null;
  const availableChartTabs = chartData ? getAvailableChartTabs(chartData, metadata) : [];
  const shownTabs = availableChartTabs.filter((tab) => visibleChartKeys.includes(tab.key));

  return (
    <div className="doe-page">
      <div className="doe-header">
        <div>
          <h1>DOE Analyzer</h1>
          <p>Design-of-Experiment statistics, charts, and an AI-generated narrative.</p>
        </div>
        {design?.designType && (
          <Tag
            severity={DESIGN_TYPE_SEVERITY[design.designType] || "info"}
            value={DESIGN_TYPE_LABELS[design.designType] || design.designType}
            style={{ fontSize: "0.9rem", padding: "0.4rem 0.8rem" }}
          />
        )}
      </div>

      <div className="doe-card">
        <h3 className="doe-card-title">AI Model</h3>
        {providerStatus.hasKey ? (
          <span>
            The AI narrative will use <strong>{describeProviderStatus(providerStatus)}</strong>. To change the provider
            or model, go to <Link to="/AISettings">AI Settings</Link>.
          </span>
        ) : (
          <span>
            Optional. The AI narrative needs an AI provider and model, which you set up on the{" "}
            <Link to="/AISettings">AI Settings</Link> page. The charts and statistics work without one.
          </span>
        )}
        <SystemPromptEditor />
      </div>

      <div className="doe-card">
        <div className="doe-column">
          <ExperimentPicker
            initialExperimentId={experiment_ids?.[0]}
            onSelect={(id, experiment) => {
              setSelectedExperimentId(id);
              setSelectedExperiment(experiment || null);
            }}
          />
          <div className="doe-row">
            <Button
              label="Analyze"
              icon="pi pi-chart-bar"
              onClick={runAnalysis}
              disabled={!selectedExperimentId || analyzing}
            />
            {analyzing && <ProgressSpinner style={{ width: "1.75rem", height: "1.75rem" }} strokeWidth="5" />}
          </div>
        </div>
        {analyzeError && <Message severity="error" text={analyzeError} style={{ marginTop: "0.75rem" }} />}
      </div>

      {chartData && chartData.status && chartData.status !== "OK" && (
        <Message severity="warn" text={`Analysis unavailable: ${chartData.status}`} />
      )}

      {chartData && (
        <>
          <div className="doe-card">
            <div className="doe-summary-strip">
              <span className="doe-stat">
                Model: <strong>{metadata.experiment_name}</strong>
              </span>
              <span className="doe-stat">
                Trials: <strong>{metadata.total_trials}</strong>
              </span>
              {design?.responseVariableLabel && (
                <span className="doe-stat">
                  Response: <strong>{design.responseVariableLabel}</strong>
                </span>
              )}
            </div>
            {design?.hasDegenerate &&
              Object.entries(design.degenerate).map(([resp, msg]) => (
                <Message key={resp} severity="warn" text={`${resp}: ${msg}`} style={{ marginTop: "0.5rem" }} />
              ))}
          </div>

          <div className="doe-card">
            <div className="doe-row" style={{ justifyContent: "space-between" }}>
              <div>
                <h3 className="doe-card-title" style={{ margin: 0 }}>
                  AI Narrative
                </h3>
                {providerStatus.hasKey && (
                  <small className="doe-muted">
                    via {describeProviderStatus(providerStatus)} · <Link to="/AISettings">change</Link>
                  </small>
                )}
              </div>
              <Button
                label="Generate AI Narrative"
                icon="pi pi-sparkles"
                onClick={generateNarrative}
                disabled={!providerStatus.hasKey || generatingNarrative}
                loading={generatingNarrative}
              />
            </div>
            <div className="doe-column" style={{ marginTop: "0.75rem" }}>
              <label htmlFor="doe-objective" className="doe-muted">
                Optional objective/question to focus the narrative on (e.g. "Find the resource
                configuration that maximizes lives saved and survival probability, while minimizing
                wait time and cost"):
              </label>
              <InputTextarea
                id="doe-objective"
                value={userObjective}
                onChange={(e) => setUserObjective(e.target.value)}
                rows={2}
                autoResize
                placeholder="What should the narrative focus on or answer?"
              />
            </div>
            {providerStatus.loaded && !providerStatus.hasKey && (
              <Message
                severity="info"
                content={
                  <span>
                    Connect an AI provider in <Link to="/AISettings">AI Settings</Link> to generate a narrative.
                  </span>
                }
                style={{ marginTop: "0.75rem" }}
              />
            )}
            {narrativeError && <Message severity="error" text={narrativeError} style={{ marginTop: "0.75rem" }} />}
            {narrative && (
              <div className="doe-narrative-panel">
                <div className="doe-narrative-toolbar">
                  <span className="doe-narrative-badge">
                    <i className="pi pi-sparkles" style={{ marginRight: "0.35rem" }} />
                    AI-generated narrative
                  </span>
                  <Button
                    label={narrativeCopied ? "Copied" : "Copy"}
                    icon={narrativeCopied ? "pi pi-check" : "pi pi-copy"}
                    text
                    size="small"
                    onClick={copyNarrative}
                  />
                </div>
                <div className="doe-narrative">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{narrative}</ReactMarkdown>
                </div>
                <NarrativeChat doeResult={narrativeInput(chartData)} narrative={narrative} userObjective={userObjective} />
              </div>
            )}
          </div>

          {chartData.fundamental_questions && (
            <div className="doe-card">
              <FundamentalQuestionsSection
                fundamentalQuestions={chartData.fundamental_questions}
                visibleKeys={visibleQuestionKeys}
                setVisibleKeys={setVisibleQuestionKeys}
              />
            </div>
          )}

          <div className="doe-card">
            <div className="doe-row" style={{ justifyContent: "space-between", marginBottom: "0.75rem" }}>
              <h3 className="doe-card-title" style={{ margin: 0 }}>
                Charts
              </h3>
              <MultiSelect
                value={visibleChartKeys}
                onChange={(e) => setVisibleChartKeys(e.value)}
                options={availableChartTabs}
                optionValue="key"
                optionLabel="label"
                display="chip"
                placeholder="Select charts to show"
                style={{ minWidth: "260px" }}
              />
            </div>
            {shownTabs.length === 0 && <div className="doe-empty-state">No charts selected.</div>}
            {shownTabs.length > 0 && (
              <TabView
                scrollable
                activeIndex={Math.min(activeChartTab, shownTabs.length - 1)}
                onTabChange={(e) => setActiveChartTab(e.index)}
              >
                {shownTabs.map((tab) => (
                  <TabPanel key={tab.key} header={tab.label}>
                    {tab.key === "best_worst_trial"
                      ? <BestWorstTrial metadata={metadata} />
                      : renderChart(chartData[tab.key], tab.label)}
                  </TabPanel>
                ))}
              </TabView>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default DOEAnalyzerPage;
